import { PlusOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Empty, Popconfirm, Typography, message } from 'antd'
import axios from 'axios'
import { sortBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor } from '@/components'
import { Report, ReportConfigurationModuleField, ReportExtra, ReportSnapshot } from '@/schemas'

interface ReportExtrasProps {
  report: Report
  editable: boolean
  snapshot?: ReportSnapshot
  extraFields: ReportConfigurationModuleField[]
  refresh?: any
}

export const ReportExtras: React.FC<ReportExtrasProps> = ({ report, editable, snapshot, extraFields, refresh }) => {
  const [queue, setQueue] = React.useState<ReportExtra[]>([])
  const [focusField, setFocusField] = React.useState('')
  const { t } = useTranslation()

  const [findField, setFindField] = React.useState<ReportConfigurationModuleField[]>([])
  const [notfindField, setNotfindField] = React.useState<ReportConfigurationModuleField[]>([])

  const getLastWeekReport = async (): Promise<ReportExtra[] | undefined> => {
    const res = await axios.request({
      url: '/api/report/getReports',
      params: {
        selfId: report.selfId,
        type: report.type,
        startDate: moment(report.startDate).isoWeekday(1).subtract(1, 'week').format('ll'),
      },
    })
    if (res.data && res.data.length > 0) {
      return res.data[0].extras
    }
    return undefined
  }

  React.useEffect(() => {
    // 过滤已被删除 且 被选择的
    extraFields = extraFields.filter((field) => !field.isDeleted && field.isChecked)
    setFindField(
      sortBy(
        extraFields.filter((field) =>
          report.extras.find((e) => (e.fieldId ? e.fieldId === field.id : e.field === field.name))
        ),
        ['order', 'createdAt']
      )
    )
    setNotfindField(
      sortBy(
        extraFields.filter(
          (field) => !report.extras.find((e) => (e.fieldId ? e.fieldId === field.id : e.field === field.name))
        ),
        ['order', 'createdAt']
      )
    )
  }, [report.selfId, report.startDate, extraFields, report.extras])

  const [reportsInfo, setReportsInfo] = React.useState<{
    [key: string]: { onGoing: boolean; lastUpdatedAt: string; resolver?: () => void; rejecter?: () => void }
  }>({})

  const newExtras = report.extras.reduce((acc, extra) => {
    return { ...acc, [extra.id]: { lastUpdatedAt: extra.updatedAt, onGoing: false } }
  }, {})

  Object.keys(newExtras).forEach((id) => {
    if (!reportsInfo[id]) {
      setReportsInfo({ ...reportsInfo, [id]: newExtras[id] })
    }
  })

  const { run: upsert } = useRequest(
    (data: ReportExtra) => ({
      url: '/api/project/upsertProjectReportExtra',
      method: 'post',
      data: {
        selfId: report.selfId,
        id: data.id,
        field: data.field,
        fieldId: data.fieldId,
        content: data.content,
        reportId: report.id,
        type: snapshot?.type,
        snapshotSelfId: snapshot?.selfId,
        lastUpdatedAt: reportsInfo?.[data.id]?.lastUpdatedAt || undefined,
      },
    }),
    {
      manual: true,
      fetchKey: (params) => params.field,
      throwOnError: true,
    }
  )

  const consumeQueue = React.useCallback(async () => {
    const [last] = queue.slice(-1)
    if (last) {
      const { id } = last
      const isOnGoing = reportsInfo[id]?.onGoing

      if (!isOnGoing) {
        if (!reportsInfo[id]) {
          reportsInfo[id] = { onGoing: false, lastUpdatedAt: '' }
        }
        reportsInfo[id].onGoing = true
        const { resolver, rejecter } = reportsInfo[id]

        const { updatedAt } = await upsert(last).catch(() => {
          if (rejecter) {
            rejecter()
          }
          setReportsInfo({ ...reportsInfo, [id]: { ...reportsInfo[id], onGoing: false, rejecter: undefined } })
        })

        setQueue((prev) => {
          const index = prev.findIndex((item) => last === item)
          const curr = prev.slice(index + 1)
          if (curr.length) {
            return curr
          }

          if (resolver) {
            resolver()
          }
          return []
        })
        setReportsInfo({ ...reportsInfo, [id]: { lastUpdatedAt: updatedAt, onGoing: false, resolver: undefined } })
      }
    }
  }, [queue, reportsInfo])

  React.useEffect(() => {
    queue.length && consumeQueue()
  }, [queue])

  const onSave = async (content: string, extra: any) => {
    const { id } = extra
    setQueue((prev) => [...prev, { ...extra, content }])

    if (!id) {
      return Promise.resolve()
    }

    const resolver = reportsInfo[id]['resolver']
    const rejecter = reportsInfo[id]['rejecter']

    if (!rejecter || !resolver) {
      return new Promise<void>((resolve, reject) => {
        reportsInfo[id].resolver = resolve
        reportsInfo[id].rejecter = reject
      })
    }
    return Promise.resolve()
  }

  const renderExtra = (field: ReportConfigurationModuleField) => {
    const extra = report.extras.find((e) => (e.fieldId ? e.fieldId === field.id : e.field === field.name))
    return (
      <div
        key={`${report.id}/${field.id}`}
        className={focusField === field.id && editable ? 'report-extra-field--focus' : ''}
      >
        {/* t('当前进展') t('问题风险') t('其他事项') t('下一步计划') */}
        <div className='flex justify-between items-center h-48px report-extra-field-title'>
          <div className='text-16px font-bold'>{t(field.name)}</div>
          {focusField === field.name && editable && (
            <Typography.Text className='text-blue-500 text-14px'>{`${moment(extra?.updatedAt)
              .utc()
              .format('YYYY.MM.DD HH:mm:ss')} ${t('updated')}`}</Typography.Text>
          )}
        </div>
        <div
          className='w-full report-extra-field-content'
          onFocus={() => {
            setFocusField(field.id)
          }}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as any)) {
              setFocusField('')
            }
          }}
        >
          <Editor
            editorKey={`${report.id}/${field.id}/${extra?.content}`}
            editable={editable}
            content={extra?.content}
            isFocus={focusField === field.id}
            onSave={async (content) => onSave(content, Object.assign({ field: field.name, fieldId: field.id }, extra))}
          />
        </div>
      </div>
    )
  }

  const addField = (field: ReportConfigurationModuleField) => {
    setFindField(findField.concat([field]))
    setFocusField(field.id)
    setNotfindField(notfindField.filter((_field) => _field.id !== field.id))
  }

  const fetchLastReport = async () => {
    const extras = await getLastWeekReport()
    if (!extras) {
      message.warn(t('report.missing'))
      return
    }
    if (extras.length === 0) {
      message.warn(t('report.nodata'))
      return
    }
    for (const extra of extras) {
      const currentExtra = report.extras.find((e) =>
        e.fieldId ? e.fieldId === extra.fieldId : e.field === extra.field
      )
      await upsert({
        ...currentExtra,
        field: extra.field,
        fieldId: extra.fieldId,
        content: extra.content,
      } as ReportExtra)
    }
    refresh()
    message.success(t('pull success'))
  }

  return (
    <div className='space-y-20px'>
      {editable && findField.length === 0 && (
        <Empty
          image='https://web-cdn.agora.io/pmo/icons/empty2.png'
          description={
            <span>
              {t('未填写周报')}
              {editable && (
                <Popconfirm title={t('report.cover')} trigger={['click']} onConfirm={() => fetchLastReport()}>
                  , <span className='cursor-pointer text-blue-600'>{t('report.pull')}</span>
                </Popconfirm>
              )}
            </span>
          }
        ></Empty>
      )}
      {!editable && findField.length === 0 && <span className='text-gray-500'>{t('本周未填写进展')}</span>}
      {findField.map((field) => renderExtra(field))}
      {editable && notfindField.length !== 0 && (
        <div className='flex flex-wrap items-center'>
          {notfindField.map((field) => (
            <div key={field.id} className='report-extra-field-block mb-10px mr-20px' onClick={() => addField(field)}>
              <PlusOutlined />
              <span className='inline-block ml-10px'>{t(field.name)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
